import React, { useCallback, useEffect, useState } from 'react';
import {
  Radio, Battery, Droplets, Power, RefreshCw, Activity, AlertTriangle,
  CheckCircle2, Clock, XCircle, Cpu, Waves, FileText, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { iotApi, IoTNode, Command, IoTAlert, DashboardStats, TelemetrySummary } from '../lib/iotApi';
import TelemetryChart, { TelemetryPoint } from '../components/TelemetryChart';
import ReportSchedules from '../components/ReportSchedules';
import {
  isTelemetryEnabled,
  isTtnSimulatorEnabled,
  supportsValveControl,
} from '../config/frontend';

const TELEMETRY_ENABLED = isTelemetryEnabled();
const TTN_SIMULATOR_ENABLED = isTtnSimulatorEnabled();

const PERIODS: { key: number; label: string }[] = [
  { key: 6, label: '6 h' },
  { key: 24, label: '24 h' },
  { key: 168, label: '7 j' },
];

const fmtMinutes = (min: number): string => {
  if (!min) return '0 min';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
};

const statusStyle = (s: string): { color: string; icon: React.ElementType; label: string } => {
  switch (s) {
    case 'OBSERVED_SUCCESS':
      return { color: 'var(--color-success)', icon: CheckCircle2, label: 'Confirmé' };
    case 'SENT_TO_TTN':
      return { color: 'var(--color-info)', icon: Radio, label: 'Envoyé à TTN' };
    case 'REQUESTED':
      return { color: 'var(--color-warning)', icon: Clock, label: 'Demandé' };
    case 'TIMEOUT':
      return { color: 'var(--color-warning)', icon: Clock, label: 'Timeout' };
    case 'OBSERVED_FAILED':
      return { color: 'var(--color-danger)', icon: XCircle, label: 'Échec' };
    default:
      return { color: 'var(--text-secondary)', icon: Clock, label: s };
  }
};

const fmtTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

const IoTNodes: React.FC = () => {
  const [nodes, setNodes] = useState<IoTNode[]>([]);
  const [commands, setCommands] = useState<Command[]>([]);
  const [alerts, setAlerts] = useState<IoTAlert[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [telemetry, setTelemetry] = useState<Record<string, TelemetryPoint[]>>({});
  const [summaries, setSummaries] = useState<Record<string, TelemetrySummary>>({});
  const [period, setPeriod] = useState<number>(24);
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [n, c, a, s] = await Promise.all([
        iotApi.get('/iot/nodes'),
        iotApi.get('/iot/commands'),
        iotApi.get('/iot/alerts'),
        iotApi.get('/iot/dashboard'),
      ]);
      setNodes(n);
      setCommands(c);
      setAlerts(a);
      setStats(s);
      if (!TELEMETRY_ENABLED) {
        setTelemetry({});
        setSummaries({});
        return;
      }
      // Windowed telemetry + aggregated summary per node
      const nodeList = n as IoTNode[];
      const series = await Promise.all(
        nodeList.map((node) =>
          iotApi.get(`/iot/nodes/${node.id}/telemetry?hours=${period}&limit=2000`)
            .then((rows) => [node.id, rows] as const).catch(() => [node.id, []] as const)
        )
      );
      const sums = await Promise.all(
        nodeList.map((node) =>
          iotApi.get(`/iot/nodes/${node.id}/telemetry/summary?hours=${period}`)
            .then((sum) => [node.id, sum] as const).catch(() => [node.id, null] as const)
        )
      );
      setTelemetry(Object.fromEntries(series));
      setSummaries(Object.fromEntries(sums.filter(([, v]) => v)));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const controlValve = async (node: IoTNode, state: 'on' | 'off') => {
    setBusy(node.id);
    try {
      await iotApi.post(`/iot/nodes/${node.id}/valve`, { state });
      toast.success(`Commande vanne « ${state === 'on' ? 'OUVRIR' : 'FERMER' } » envoyée à ${node.name}`);
      setTimeout(refresh, 800);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const simulate = async (node: IoTNode, valve: number) => {
    try {
      await iotApi.post(`/ttn/simulate/${node.id}?valve=${valve}`);
      toast.success(`Relevé simulé reçu de ${node.name}`);
      setTimeout(refresh, 500);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const ackAlert = async (id: string) => {
    try {
      await iotApi.post(`/iot/alerts/${id}/ack`);
      refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const exportReport = async (
    scope: 'node' | 'sector',
    id: string,
    fmt: 'csv' | 'pdf',
    label: string,
  ) => {
    try {
      const path =
        scope === 'node'
          ? `/iot/nodes/${id}/report?hours=${period}&format=${fmt}`
          : `/iot/sectors/${id}/report?hours=${period}&format=${fmt}`;
      await iotApi.download(path, `bilan-${scope}.${fmt}`);
      toast.success(`Export ${fmt.toUpperCase()} (${label}) généré`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const statCards = [
    { label: 'Nodes', value: stats?.nodes_total ?? 0, color: 'var(--color-primary)', icon: Cpu },
    { label: 'Vannes ouvertes', value: stats?.valves_open ?? 0, color: 'var(--color-success)', icon: Droplets },
    { label: 'Alertes actives', value: stats?.open_alerts ?? 0, color: 'var(--color-warning)', icon: AlertTriangle },
    { label: 'Commandes confirmées', value: stats?.commands_success ?? 0, color: 'var(--color-info)', icon: CheckCircle2 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]">Nodes IoT — TTN / LoRaWAN</h1>
          <p className="text-body text-[var(--text-secondary)] mt-1">
            Télémétrie en direct et contrôle des vannes (Makerfabs AgroSense Valve &amp; Water Flow)
          </p>
        </div>
        <button
          onClick={refresh}
          className="flex items-center gap-2 px-4 py-2 border border-[var(--border-medium)] rounded-lg hover:bg-[var(--gray-50)]"
          data-testid="iot-refresh"
        >
          <RefreshCw className="w-4 h-4" /> Actualiser
        </button>
      </div>

      {!TELEMETRY_ENABLED && (
        <div className="card border border-[var(--color-info)] bg-blue-50 text-blue-800">
          Telemetry disabled in current AWS dev environment
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <div key={i} className="card text-center" data-testid={`iot-stat-${i}`}>
            <s.icon className="w-6 h-6 mx-auto mb-2" style={{ color: s.color }} />
            <div className="text-2xl font-bold mb-1" style={{ color: s.color }}>{s.value}</div>
            <div className="text-sm text-[var(--text-secondary)]">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      {alerts.filter((a) => a.status === 'open').length > 0 && (
        <div className="space-y-2">
          {alerts.filter((a) => a.status === 'open').map((a) => (
            <div key={a.id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30" data-testid={`iot-alert-${a.id}`}>
              <div className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                <AlertTriangle className="w-4 h-4 text-[var(--color-warning)]" />
                {a.message}
              </div>
              <button onClick={() => ackAlert(a.id)} className="text-xs px-3 py-1 rounded-md border border-[var(--border-medium)] hover:bg-[var(--gray-50)]" data-testid={`iot-alert-ack-${a.id}`}>
                Acquitter
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Nodes */}
      {loading ? (
        <div className="text-center py-12 text-[var(--text-secondary)]">Chargement…</div>
      ) : nodes.length === 0 ? (
        <div className="text-center py-12" data-testid="iot-empty">
          <Radio className="w-12 h-12 text-[var(--text-secondary)] mx-auto mb-4" />
          <p className="text-body text-[var(--text-secondary)]">Aucun node enregistré</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {nodes.map((node) => {
            const m = node.latest?.measures || {};
            const valveOpen = (node.valve_state ?? m.valve_state) === 1;
            const bat = node.battery_v ?? m.battery_v;
            const valveCapable = supportsValveControl(node.model);
            return (
              <div key={node.id} className="card" data-testid={`iot-node-${node.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-[var(--color-primary)]/10">
                      <Radio className="w-5 h-5 text-[var(--color-primary)]" />
                    </div>
                    <div>
                      <h3 className="text-heading-4 text-[var(--text-primary)]">{node.name}</h3>
                      <p className="text-xs text-[var(--text-tertiary)] font-mono">DevEUI {node.dev_eui}</p>
                      <p className="text-xs text-[var(--text-tertiary)]">{node.model}</p>
                    </div>
                  </div>
                  {valveCapable && <span
                    className="px-2 py-1 text-xs rounded-full text-white"
                    style={{ backgroundColor: valveOpen ? 'var(--color-success)' : 'var(--text-secondary)' }}
                    data-testid={`iot-valve-state-${node.id}`}
                  >
                    Vanne {valveOpen ? 'OUVERTE' : 'FERMÉE'}
                  </span>}
                </div>

                {/* Live metrics */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="text-center p-3 bg-[var(--gray-50)] rounded-lg">
                    <Battery className="w-5 h-5 mx-auto mb-1 text-[var(--color-info)]" />
                    <div className="text-sm font-semibold text-[var(--text-primary)]">{bat != null ? `${bat} V` : '—'}</div>
                    <div className="text-xs text-[var(--text-secondary)]">Batterie</div>
                  </div>
                  <div className="text-center p-3 bg-[var(--gray-50)] rounded-lg">
                    <Waves className="w-5 h-5 mx-auto mb-1 text-[var(--color-primary)]" />
                    <div className="text-sm font-semibold text-[var(--text-primary)]">{m.flow_velocity_lpm != null ? `${m.flow_velocity_lpm} L/min` : '—'}</div>
                    <div className="text-xs text-[var(--text-secondary)]">Débit</div>
                  </div>
                  <div className="text-center p-3 bg-[var(--gray-50)] rounded-lg">
                    <Activity className="w-5 h-5 mx-auto mb-1 text-[var(--color-accent)]" />
                    <div className="text-sm font-semibold text-[var(--text-primary)]">{m.reporting_interval_s != null ? `${m.reporting_interval_s}s` : '—'}</div>
                    <div className="text-xs text-[var(--text-secondary)]">Intervalle</div>
                  </div>
                </div>
                <p className="text-xs text-[var(--text-tertiary)] mb-4">Dernier relevé : {fmtTime(node.latest?.ts)}</p>

                {/* 24h telemetry chart */}
                {TELEMETRY_ENABLED && <div className="mb-4 pt-2 border-t border-[var(--border-light)]">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-[var(--text-secondary)]">Télémétrie</p>
                    <div className="flex items-center gap-1" data-testid={`period-selector-${node.id}`}>
                      {PERIODS.map((p) => (
                        <button
                          key={p.key}
                          onClick={() => setPeriod(p.key)}
                          className={`px-2 py-0.5 text-xs rounded-md border transition-colors ${
                            period === p.key
                              ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
                              : 'bg-[var(--surface)] text-[var(--text-secondary)] border-[var(--border-light)] hover:bg-[var(--gray-50)]'
                          }`}
                          data-testid={`period-${p.key}-${node.id}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <TelemetryChart data={telemetry[node.id] || []} />

                  {/* Aggregated summary */}
                  {summaries[node.id] && (
                    <div className="grid grid-cols-3 gap-2 mt-3" data-testid={`telemetry-summary-${node.id}`}>
                      {[
                        { label: 'Volume total', value: `${summaries[node.id].total_volume_l} L`, color: 'var(--color-info)' },
                        { label: 'Débit moyen', value: `${summaries[node.id].avg_flow_lpm} L/min`, color: 'var(--color-primary)' },
                        { label: 'Débit max', value: `${summaries[node.id].max_flow_lpm} L/min`, color: 'var(--color-primary)' },
                        { label: 'Vanne ouverte', value: fmtMinutes(summaries[node.id].valve_open_minutes), color: 'var(--color-success)' },
                        { label: 'Batterie min', value: summaries[node.id].battery_min_v != null ? `${summaries[node.id].battery_min_v} V` : '—', color: 'var(--text-primary)' },
                        { label: 'Batterie max', value: summaries[node.id].battery_max_v != null ? `${summaries[node.id].battery_max_v} V` : '—', color: 'var(--text-primary)' },
                      ].map((s, i) => (
                        <div key={i} className="bg-[var(--gray-50)] rounded-lg p-2 text-center">
                          <div className="text-sm font-semibold" style={{ color: s.color }}>{s.value}</div>
                          <div className="text-[10px] text-[var(--text-secondary)]">{s.label}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Export water balance */}
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <span className="text-xs text-[var(--text-secondary)]">Bilan hydrique :</span>
                    <button
                      onClick={() => exportReport('node', node.id, 'csv', node.name)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-[var(--border-medium)] hover:bg-[var(--gray-50)]"
                      data-testid={`export-node-csv-${node.id}`}
                    >
                      <FileDown className="w-3.5 h-3.5" /> CSV
                    </button>
                    <button
                      onClick={() => exportReport('node', node.id, 'pdf', node.name)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-[var(--border-medium)] hover:bg-[var(--gray-50)]"
                      data-testid={`export-node-pdf-${node.id}`}
                    >
                      <FileText className="w-3.5 h-3.5" /> PDF
                    </button>
                    {node.sector_id && (
                      <button
                        onClick={() => exportReport('sector', node.sector_id as string, 'pdf', node.sector_name || 'secteur')}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-dashed border-[var(--border-medium)] text-[var(--text-secondary)] hover:bg-[var(--gray-50)]"
                        data-testid={`export-sector-pdf-${node.id}`}
                        title={`Bilan du secteur ${node.sector_name || ''}`}
                      >
                        <FileText className="w-3.5 h-3.5" /> Secteur PDF
                      </button>
                    )}
                  </div>
                </div>}

                {/* Valve control */}
                {valveCapable && <div className="flex flex-wrap gap-2 pt-4 border-t border-[var(--border-light)]">
                  <button
                    onClick={() => controlValve(node, 'on')}
                    disabled={busy === node.id}
                    className="flex-1 min-w-[110px] flex items-center justify-center gap-2 px-3 py-2 bg-[var(--color-success)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                    data-testid={`iot-valve-on-${node.id}`}
                  >
                    <Power className="w-4 h-4" /> Ouvrir
                  </button>
                  <button
                    onClick={() => controlValve(node, 'off')}
                    disabled={busy === node.id}
                    className="flex-1 min-w-[110px] flex items-center justify-center gap-2 px-3 py-2 bg-[var(--color-danger)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                    data-testid={`iot-valve-off-${node.id}`}
                  >
                    <Power className="w-4 h-4" /> Fermer
                  </button>
                  {TTN_SIMULATOR_ENABLED && <button
                    onClick={() => simulate(node, valveOpen ? 0 : 1)}
                    className="px-3 py-2 border border-dashed border-[var(--border-medium)] rounded-lg text-xs text-[var(--text-secondary)] hover:bg-[var(--gray-50)]"
                    title="DEV : simuler un relevé LoRaWAN"
                    data-testid={`iot-simulate-${node.id}`}
                  >
                    Simuler relevé
                  </button>}
                </div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Command ledger */}
      <div>
        <h2 className="text-heading-4 text-[var(--text-primary)] mb-3">Historique des commandes (ledger)</h2>
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm" data-testid="iot-command-table">
            <thead>
              <tr className="border-b border-[var(--border-light)] text-left text-[var(--text-secondary)]">
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Paramètres</th>
                <th className="px-4 py-3 font-medium">FPort</th>
                <th className="px-4 py-3 font-medium">Payload</th>
                <th className="px-4 py-3 font-medium">Demandé</th>
                <th className="px-4 py-3 font-medium">Statut</th>
              </tr>
            </thead>
            <tbody>
              {commands.slice(0, 15).map((c) => {
                const st = statusStyle(c.status);
                return (
                  <tr key={c.id} className="border-b border-[var(--border-light)] last:border-0" data-testid={`iot-command-${c.id}`}>
                    <td className="px-4 py-3 text-[var(--text-primary)]">{c.type}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{JSON.stringify(c.params)}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{c.f_port}</td>
                    <td className="px-4 py-3 font-mono text-[var(--text-secondary)]">0x{c.payload_hex}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{fmtTime(c.requested_at)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: st.color }}>
                        <st.icon className="w-4 h-4" /> {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {commands.length === 0 && (
            <div className="text-center py-8 text-[var(--text-secondary)]">Aucune commande</div>
          )}
        </div>
      </div>

      {/* Automated report schedules */}
      <ReportSchedules />
    </div>
  );
};

export default IoTNodes;
